import { useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import {
  isDesktopNotificationPreferenceEnabled,
  isDesktopNotificationSupported,
  showDesktopNotification,
} from '../lib/desktopNotifications'

function snippet(text: string | null | undefined, max = 80): string {
  const t = (text || '').replace(/\s+/g, ' ').trim()
  if (!t) return 'New message'
  return t.length > max ? `${t.slice(0, max)}…` : t
}

/**
 * Desktop OS notifications for inbound SMS/email and team chat inserts.
 * Respects localStorage preference; skips when the matching thread is open and focused.
 */
export function useDesktopMessageNotifications(enabled: boolean) {
  const navigate = useNavigate()
  const location = useLocation()
  const openKeyRef = useRef<string | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const path = location.pathname
    if (path.startsWith('/messages')) {
      const tab = params.get('tab') || 'external'
      if (tab === 'internal' && params.get('channel')) {
        openKeyRef.current = `chat:${params.get('channel')}`
      } else {
        const id = params.get('id') || path.match(/^\/messages\/([^/]+)/)?.[1] || null
        openKeyRef.current = id ? `convo:${id}` : null
      }
    } else if (path.startsWith('/inbox/')) {
      openKeyRef.current = `convo:${path.slice('/inbox/'.length)}`
    } else if (path.startsWith('/team-chat')) {
      const ch = params.get('channel')
      openKeyRef.current = ch ? `chat:${ch}` : null
    } else {
      openKeyRef.current = null
    }
  }, [location.pathname, location.search])

  useEffect(() => {
    if (!enabled) return
    if (!isDesktopNotificationSupported()) return
    if (!isDesktopNotificationPreferenceEnabled()) return
    if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') return

    const channel = supabase
      .channel('desktop-msg-notify')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          const row = payload.new as {
            direction?: string
            conversation_id?: string
            contact_name?: string | null
            body?: string | null
            subject?: string | null
          }
          if (row?.direction !== 'inbound' || !row.conversation_id) return
          const suppressKey = `convo:${row.conversation_id}`
          showDesktopNotification({
            title: row.contact_name || 'New message',
            body: snippet(row.body || row.subject),
            tag: `inbox-${row.conversation_id}`,
            suppressKey,
            openThreadKey: openKeyRef.current,
            onClick: () => {
              navigate(`/messages?tab=external&id=${row.conversation_id}`)
            },
          })
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_messages' },
        (payload) => {
          const row = payload.new as {
            channel_id?: string
            body?: string | null
            sender_id?: string
          }
          if (!row?.channel_id) return
          const suppressKey = `chat:${row.channel_id}`
          showDesktopNotification({
            title: 'Team chat',
            body: snippet(row.body),
            tag: `chat-${row.channel_id}`,
            suppressKey,
            openThreadKey: openKeyRef.current,
            onClick: () => {
              navigate(`/messages?tab=internal&channel=${row.channel_id}`)
            },
          })
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [enabled, navigate])
}
